import { useNotification } from "@expert/common-ui";
import { Flex, Group, Stack } from "@mantine/core";
import type { CountryCode } from "libphonenumber-js";
import PQueue from "p-queue";
import { type JSX, useEffect } from "react";
import { sdkEventBus, useAgentSdk, useCallbackState, type VoiceTask } from "@/sdk";
import { CallbackInfoBanner } from "../ScheduleCallback";
import { TransferCall } from "../TransferCall/TransferCall";
import { verifyAndPlayWhisper } from "../utils/whisperUtils";
import classes from "./CallControls.module.css";
import { CallControlsSwitch } from "./CallControlsSwitch";
import { CallDetailsSelector } from "./CallDetailsSelector";
import { useControlsStore } from "./controls.store";

const BUFFER_INTERVAL = 300;
const dialQueue = new PQueue({ concurrency: 1, intervalCap: 1, interval: BUFFER_INTERVAL });

interface CallControlsProps {
    voiceTask: VoiceTask;
    isInternationalPhoneEnabled: boolean;
    defaultCountry: CountryCode;
    clientEnabledCountries: CountryCode[];
    isVdnTransferInputEnabled: boolean;
    isSegmentedTransferMenuEnabled: boolean;
    countrySelectEnabled: boolean;
}

export function CallControls({
    voiceTask,
    isInternationalPhoneEnabled,
    defaultCountry,
    clientEnabledCountries,
    isVdnTransferInputEnabled,
    isSegmentedTransferMenuEnabled,
    countrySelectEnabled,
}: CallControlsProps): JSX.Element {
    const agentSdk = useAgentSdk();
    const callbackState = useCallbackState();
    const notification = useNotification();

    const {
        clearPanelsState,
        dialpadInput,
        transferCallActive,
        dialpadNotification,
        isConference,
        setIsConference,
        setDialpadNotification,
        resetState: resetControlsStoreState,
    } = useControlsStore();

    useEffect(() => {
        if (["wrapping", "completed", "cancelled"].includes(voiceTask.status)) {
            clearPanelsState();
            setIsConference(false);

            if (dialpadNotification) {
                notification.hide(dialpadNotification);
                setDialpadNotification(undefined);
            }
        }

        // Whenever the main task is in a pending state, that means we're accepting a call
        // and it has to be the first call in this session (since a conference can't have been started yet).
        // If that's the case, then we reset the call controls state here so the call controls always
        // start in the default state at the beginning of a call
        if (voiceTask.status === "pending") {
            resetControlsStoreState();
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [voiceTask.status]);

    useEffect(() => {
        if (dialpadInput.length > 0) {
            const dial = dialpadInput[dialpadInput.length - 1];
            void dialQueue.add(() => agentSdk.sendDtmfDigits(dial));
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [dialpadInput]);

    useEffect(() => {
        const unsubCallAccepted = sdkEventBus.on("call_accepted", (newVoiceTask: VoiceTask) => {
            verifyAndPlayWhisper(newVoiceTask.whisper);
        });

        return () => {
            unsubCallAccepted();
        };
    }, []);

    useEffect(() => {
        const hasActiveParticipants = voiceTask.conferenceParticipants.some(({ type }) => type !== "inactive");

        if (hasActiveParticipants) {
            if (!isConference) {
                clearPanelsState();
                setIsConference(true);
            }
        } else {
            setIsConference(false);
        }
    }, [voiceTask.conferenceParticipants, isConference, clearPanelsState, setIsConference]);

    return (
        <Stack data-testid="active-task" gap="xs">
            <Group className={classes.controlsRow}>
                <CallDetailsSelector task={voiceTask} />
                <Flex flex="1" justify="flex-end" miw="fit-content">
                    <Group align="center" data-testid={`call-controls-group-${voiceTask.status}`} justify="flex-end">
                        <CallControlsSwitch task={voiceTask} />
                    </Group>
                </Flex>
            </Group>
            {callbackState && voiceTask.status === "assigned" ? <CallbackInfoBanner /> : null}
            {transferCallActive ? (
                <TransferCall
                    voiceTask={voiceTask}
                    isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                    defaultCountry={defaultCountry}
                    clientEnabledCountries={clientEnabledCountries}
                    isVdnTransferInputEnabled={isVdnTransferInputEnabled}
                    isSegmentedTransferMenuEnabled={isSegmentedTransferMenuEnabled}
                    countrySelectEnabled={countrySelectEnabled}
                />
            ) : null}
        </Stack>
    );
}
